import { useQuery, useMutation, useQueryClient, type UseMutationOptions } from '@tanstack/react-query';
import { getRbacService } from '@src/container';
import type { CreateRoleData } from '@src/domain/rbac/value_objects/create_role_data';
import type { UpdateRoleData } from '@src/domain/rbac/value_objects/update_role_data';

const ROLES_QUERY_KEY = 'roles';

/**
 * Hook to get all roles
 */
export const useRoles = () => {
    const rbacService = getRbacService();

    return useQuery({
        queryKey: [ROLES_QUERY_KEY],
        queryFn: () => rbacService.listAllRoles.execute(),
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
};

/**
 * Hook to get a role by ID
 */
export const useRole = (roleId: string) => {
    const rbacService = getRbacService();

    return useQuery({
        queryKey: [ROLES_QUERY_KEY, roleId],
        queryFn: () => rbacService.getRoleById.execute(roleId),
        enabled: !!roleId,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
};

/**
 * Hook to create a role
 */
export const useCreateRole = () => {
    const rbacService = getRbacService();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: CreateRoleData) => rbacService.createRole.execute(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [ROLES_QUERY_KEY] });
        },
    });
};

/**
 * Hook to update a role
 */
export const useUpdateRole = () => {
    const rbacService = getRbacService();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ roleId, data }: { roleId: string; data: UpdateRoleData }) =>
            rbacService.updateRole.execute(roleId, data),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: [ROLES_QUERY_KEY] });
            queryClient.invalidateQueries({ queryKey: [ROLES_QUERY_KEY, variables.roleId] });
        },
    });
};

/**
 * Hook to delete a role
 */
export const useDeleteRole = () => {
    const rbacService = getRbacService();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (roleId: string) => rbacService.deleteRole.execute(roleId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [ROLES_QUERY_KEY] });
        },
    });
};

/**
 * Hook to assign permissions to a role
 */
export const useAssignPermissionsToRole = () => {
    const rbacService = getRbacService();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ roleId, permissionNames }: { roleId: string; permissionNames: string[] }) =>
            rbacService.assignPermissionsToRole.execute(roleId, permissionNames),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: [ROLES_QUERY_KEY] });
            queryClient.invalidateQueries({ queryKey: [ROLES_QUERY_KEY, variables.roleId] });
        },
    });
};

/**
 * Hook to remove permissions from a role
 */
export const useRemovePermissionsFromRole = () => {
    const rbacService = getRbacService();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ roleId, permissionNames }: { roleId: string; permissionNames: string[] }) =>
            rbacService.removePermissionsFromRole.execute(roleId, permissionNames),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: [ROLES_QUERY_KEY] });
            queryClient.invalidateQueries({ queryKey: [ROLES_QUERY_KEY, variables.roleId] });
        },
    });
};

/**
 * Hook to assign multiple roles to a user
 */
export const useAssignRolesToUser = (
    options?: Omit<
        UseMutationOptions<void, Error, { userId: string; roleIds: string[] }>,
        'mutationFn'
    >
) => {
    const rbacService = getRbacService();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ userId, roleIds }: { userId: string; roleIds: string[] }) =>
            rbacService.assignRolesToUser.execute(userId, roleIds),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['users'] });
            queryClient.invalidateQueries({ queryKey: [ROLES_QUERY_KEY] });
        },
        onError: (error: any) => {
            console.error('Failed to assign roles:', error);
        },
        ...options,
    });
};

/**
 * Hook to remove multiple roles from a user
 */
export const useRemoveRolesFromUser = (
    options?: Omit<
        UseMutationOptions<void, Error, { userId: string; roleIds: string[] }>,
        'mutationFn'
    >
) => {
    const rbacService = getRbacService();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ userId, roleIds }: { userId: string; roleIds: string[] }) =>
            rbacService.removeRolesFromUser.execute(userId, roleIds),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['users'] });
            queryClient.invalidateQueries({ queryKey: [ROLES_QUERY_KEY] });
        },
        onError: (error: any) => {
            console.error('Failed to remove roles:', error);
        },
        ...options,
    });
};
